import { useRef, type ChangeEvent, type KeyboardEvent, type ReactNode } from 'react';

import type { TextFieldProps } from '@mui/material/TextField';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

import { IconButton } from '@src/shared/ui/Button';
import { Box } from '@src/shared/ui/layout';
import Typography from '@src/shared/ui/Typography';

import InputAdornment from './InputAdornment';
import TextInput from './TextInput';

const StyleSheet = {
  arrows: {
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'stretch',
  },
  arrow: {
    borderRadius: 0,
    height: 15,
    p: 0,
  },
};

type NumberInputProps = Omit<TextFieldProps, 'type'> & {
  suffix?: ReactNode;
  min?: number;
  max?: number;
  step?: number;
};

function NumberInput(props: NumberInputProps) {
  const {
    suffix,
    min,
    max,
    step = 1,
    value,
    onChange,
    slotProps,
    ...rest
  } = props;

  const htmlInputSlotProps = (slotProps?.htmlInput ?? {}) as Record<string, unknown>;
  const inputSlotProps = (slotProps?.input ?? {}) as Record<string, unknown>;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey) return;
    const allowed = /[\d.]|Backspace|Delete|ArrowLeft|ArrowRight|Tab|Home|End/;
    if (!allowed.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleStep = (delta: number) => {
    const input = inputRef.current;
    if (!input) {
      return;
    }

    const currentValue = parseFloat(input.value) || 0;
    const next = Math.round((currentValue + delta) * 1e2) / 1e2;

    if (min !== undefined && next < min) {
      return;
    }

    if (max !== undefined && next > max) {
      return;
    }

    const nextStr = String(next);
    
    input.value = nextStr;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));

    onChange?.({
      target: { value: nextStr },
      currentTarget: { value: nextStr },
    } as ChangeEvent<HTMLInputElement>);
  };

  return (
    <TextInput
      ref={rootRef}
      value={value}
      onChange={onChange}
      slotProps={{
        ...slotProps,
        htmlInput: {
          inputMode: 'numeric',
          ref: inputRef,
          onKeyDown: handleKeyDown,
          ...htmlInputSlotProps,
        },
        input: {
          ...inputSlotProps,
          endAdornment: (
            <InputAdornment position="end">
              {suffix ? (
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ mr: 0.25, userSelect: 'none' }}
                >
                  {suffix}
                </Typography>
              ) : null}
              <Box sx={ StyleSheet.arrows }>
                <IconButton
                  aria-label="Increase value"
                  size="small"
                  sx={{ ...StyleSheet.arrow, alignItems: 'flex-start' }}
                  onClick={() => handleStep(step)}
                >
                  <ArrowDropUpIcon fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label="Decrease value"
                  size="small"
                  sx={{ ...StyleSheet.arrow, alignItems: 'flex-end' }}
                  onClick={() => handleStep(-step)}
                >
                  <ArrowDropDownIcon fontSize="small" />
                </IconButton>
              </Box>
            </InputAdornment>
          ),
        },
      }}
      {...rest}
    />
  );
}

export default NumberInput;
